/**
 * Per-entity loading strategy for the Reference Cache system.
 *
 * Strategies:
 *   'bulk'         – Full list fetched on login / hydrateAll. Kept in IndexedDB.
 *                    Socket invalidation re-fetches the whole entity.
 *                    Best for: small, stable lists (< ~2 000 rows).
 *
 *   'lazy'         – NOT fetched on initial hydration. Loaded in full the first
 *                    time any UI component requests it (e.g. dropdown opens).
 *                    After that behaves like bulk (cached + socket refresh).
 *                    Best for: medium lists that only some pages need.
 *
 *   'hybrid'       – A small "seed" set (top N most recently active items) is
 *                    fetched on login via GET /reference-data/seed. Dropdowns
 *                    show the seed instantly; typing triggers server-side search
 *                    via GET /reference-data/suggest. Label resolution uses
 *                    POST /reference-data/resolve. Seed is persisted in IndexedDB
 *                    so repeat page loads feel instant.
 *                    Options: { seedSize: 50 } (default 50, max 200).
 *                    Best for: medium-large lists where you want instant dropdown
 *                    items AND search (e.g. users growing beyond 2 000).
 *
 *   'remoteSearch' – Never fully cached. Dropdowns use server-side
 *                    GET /reference-data/suggest?entity=<name>&q=<term>&limit=50
 *                    Label resolution uses POST /reference-data/resolve.
 *                    Best for: large / unbounded lists (10 000+ users, assets…).
 *
 *   'resolveOnly'  – No full list, no search. Only resolves individual IDs via
 *                    POST /reference-data/resolve (result cached in lookup map).
 *                    Best for: entities shown only as labels inside tables.
 *
 * Changing a strategy requires NO code changes elsewhere — HydrationManager,
 * ReferenceLabel, useReferenceSearch all read this config at runtime.
 */

export const LOAD_STRATEGIES = Object.freeze({
    BULK: 'bulk',
    LAZY: 'lazy',
    HYBRID: 'hybrid',
    REMOTE_SEARCH: 'remoteSearch',
    RESOLVE_ONLY: 'resolveOnly',
});

const DEFAULT_SEED_SIZE = 50;

export const REFERENCE_CONFIG = {
    unit:      { strategy: LOAD_STRATEGIES.BULK },
    role:            { strategy: LOAD_STRATEGIES.BULK },
    user:            { strategy: LOAD_STRATEGIES.BULK },
    company:         { strategy: LOAD_STRATEGIES.BULK },
    team:            { strategy: LOAD_STRATEGIES.BULK },
    process:         { strategy: LOAD_STRATEGIES.BULK },
    asset:           { strategy: LOAD_STRATEGIES.REMOTE_SEARCH },
    assetCategory:   { strategy: LOAD_STRATEGIES.BULK },
    assetGroup:      { strategy: LOAD_STRATEGIES.BULK },
    documentType:    { strategy: LOAD_STRATEGIES.BULK },
    classification: { strategy: LOAD_STRATEGIES.BULK },
    ticketCategory:  { strategy: LOAD_STRATEGIES.BULK },
    ticketSLA:       { strategy: LOAD_STRATEGIES.BULK },
    threat:          { strategy: LOAD_STRATEGIES.BULK },
    weakness:        { strategy: LOAD_STRATEGIES.HYBRID },
    vendor:          { strategy: LOAD_STRATEGIES.BULK },

    /** KVKK / BIGDES lists — bulk cache replaces per-module GET …/dropdown/params for UI pickers */
    legalReason:          { strategy: LOAD_STRATEGIES.BULK },
    collectionMethod:     { strategy: LOAD_STRATEGIES.BULK },
    processingPurpose:    { strategy: LOAD_STRATEGIES.BULK },
    subjectPersonGroup:   { strategy: LOAD_STRATEGIES.BULK },
    personalDataCategory: { strategy: LOAD_STRATEGIES.BULK },

    regulatoryClause: { strategy: LOAD_STRATEGIES.BULK },
    regulatorySource: { strategy: LOAD_STRATEGIES.BULK },
    regulatoryObligation: { strategy: LOAD_STRATEGIES.BULK },
    complianceFramework: { strategy: LOAD_STRATEGIES.BULK },
    privacyRetentionPeriod:    { strategy: LOAD_STRATEGIES.BULK },
    privacyDpiaTemplate:       { strategy: LOAD_STRATEGIES.BULK },
    governanceReportDefinition: { strategy: LOAD_STRATEGIES.BULK },
    formTemplate:              { strategy: LOAD_STRATEGIES.BULK },
    vendorAssessment:          { strategy: LOAD_STRATEGIES.BULK },
    /** Legacy IntegrationConnector catalog (page is a redirect; keep key parity with API registry). */
    integrationConnector:      { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },

    mailTemplate: { strategy: LOAD_STRATEGIES.BULK },
    rootCause: { strategy: LOAD_STRATEGIES.BULK },
    transferType: { strategy: LOAD_STRATEGIES.BULK },
    internationalTransfer: { strategy: LOAD_STRATEGIES.BULK },
    recipientGroup: { strategy: LOAD_STRATEGIES.BULK },
    dataDisposalMethod: { strategy: LOAD_STRATEGIES.BULK },
    dataSource: { strategy: LOAD_STRATEGIES.BULK },
    privacyPrinciple: { strategy: LOAD_STRATEGIES.BULK },
    securityMeasure: { strategy: LOAD_STRATEGIES.BULK },
    applicationMethod: { strategy: LOAD_STRATEGIES.BULK },
    applicationRequestInformation: { strategy: LOAD_STRATEGIES.BULK },
    explicitConsentMethod: { strategy: LOAD_STRATEGIES.BULK },
    explicitConsentReason: { strategy: LOAD_STRATEGIES.BULK },

    applicationTracking: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    dataSubjectRequest: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    dataBreach: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    explicitConsent: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    consentRecord: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    cookieInventory: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    privacyNotice: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    dpiaAssessment: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    dataProcessingAgreement: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    disposalRun: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    kvkkCompany: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },

    delegation: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    partyTemplate: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    workflowTemplate: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    assignmentTemplate: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },

    context: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    communication: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    meeting: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    interestedParty: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    scopeDocument: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },

    control: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    objective: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    competence: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    assessmentQuestion: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    vulnerability: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    training: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    assessment: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    auditPlan: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },

    incident: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    incidentTemplate: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    dutyDefinition: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    action: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    changeRequest: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    problem: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    ticket: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    ticketTemplate: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    serviceCatalogItem: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },

    bia: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    bcp: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    crisisEvent: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    exercise: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },

    document: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    webhookSubscription: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    workflowInstance: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },

    assetDisposal: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    assetLicense: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    assetLifecycleEvent: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    personalDataInventory: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },

    riskRegister: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    riskTreatment: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },

    assetGroupCriticalityEvaluation: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    detailedReportProfile: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    customFieldDefinition: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    entityFormLayout: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    customPermissionDefinition: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    tenantFeatureFlag: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    communicationProvider: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    reportArchive: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    bigdesRoadMapWorkPackage: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    bigdesCompensatoryControl: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    bigdesMeasure: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    assetGroupMeasureApplicability: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    bigdesImplementationProcessEvaluation: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    bigdesCurrentSituationEvaluationScope: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    formSubmission: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    integrationConnection: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
    deadLetterEvent: { strategy: LOAD_STRATEGIES.RESOLVE_ONLY },
};

export function getEntityConfig(entity) {
    return REFERENCE_CONFIG[entity] || { strategy: LOAD_STRATEGIES.BULK };
}

export function getEntityStrategy(entity) {
    return getEntityConfig(entity).strategy;
}

export function getEntitySeedSize(entity) {
    const cfg = getEntityConfig(entity);
    return cfg.seedSize || DEFAULT_SEED_SIZE;
}

export function getEntitiesByStrategy(strategy) {
    return Object.entries(REFERENCE_CONFIG)
        .filter(([, cfg]) => cfg.strategy === strategy)
        .map(([entity]) => entity);
}

export function isBulkEntity(entity) {
    return getEntityStrategy(entity) === LOAD_STRATEGIES.BULK;
}

export function isFullListEntity(entity) {
    const s = getEntityStrategy(entity);
    return s === LOAD_STRATEGIES.BULK || s === LOAD_STRATEGIES.LAZY;
}

export function isSearchableEntity(entity) {
    const s = getEntityStrategy(entity);
    return s === LOAD_STRATEGIES.REMOTE_SEARCH || s === LOAD_STRATEGIES.HYBRID;
}

export function needsResolve(entity) {
    const s = getEntityStrategy(entity);
    return s === LOAD_STRATEGIES.REMOTE_SEARCH
        || s === LOAD_STRATEGIES.RESOLVE_ONLY
        || s === LOAD_STRATEGIES.HYBRID;
}

export const REFERENCE_ENTITIES = Object.keys(REFERENCE_CONFIG);

/** Core references always hydrated for eligible users. */
export const REFERENCE_CORE_ENTITIES = Object.freeze([
    'unit',
    'team',
    'user',
    'company',
    'process',
    'role',
    'threat',
    'weakness',
]);

/**
 * Page permission key -> required reference entities.
 * Used to build a dependency-aware bulk hydration list.
 */
export const REFERENCE_PAGE_DEPENDENCIES = Object.freeze({
    userOperationsRoleManagement: ['role'],
    userOperationsUserManagement: ['role', 'unit', 'team', 'company'],

    // `role` feeds party ownership pickers; `vendor` / `ticketSLA` back form selects
    // so the asset form does not re-fetch them through legacy dropdown endpoints.
    assetInventoryAssets: ['assetCategory', 'assetGroup', 'classification', 'unit', 'team', 'user', 'company', 'process', 'role', 'vendor', 'ticketSLA'],
    assetInventoryLicenseManagement: ['assetLicense', 'assetCategory', 'assetGroup', 'classification', 'unit', 'team', 'user', 'company', 'process'],
    assetInventoryDisposalManagement: ['assetDisposal', 'assetCategory', 'assetGroup', 'classification', 'unit', 'team', 'user', 'company', 'process'],
    assetInventoryLifecycleEvents: ['assetLifecycleEvent', 'assetCategory', 'assetGroup', 'classification', 'unit', 'team', 'user', 'company', 'process'],
    assetInventoryMaintenance: ['asset', 'assetCategory', 'assetGroup', 'classification', 'unit', 'team', 'user', 'company', 'process'],
    assetInventoryAssetCategory: ['assetCategory', 'assetGroup', 'user', 'team', 'unit', 'role'],
    assetInventoryAssetGroup: ['assetGroup', 'unit', 'user'],
    assetInventoryPdInventory: [
        'personalDataInventory',
        'assetCategory',
        'assetGroup',
        'classification',
        'personalDataCategory',
        'processingPurpose',
        'legalReason',
        'collectionMethod',
        'subjectPersonGroup',
        'recipientGroup',
        'privacyRetentionPeriod',
        // Party ownership pickers (AssetPartyRolePickers / buildOwnerGroupedOptions)
        'user',
        'team',
        'unit',
        'role',
    ],

    definitionsGeneralSystemUnit: ['unit', 'company'],
    definitionsGeneralSystemTeam: ['team', 'unit'],
    definitionsGeneralSystemDutyDefinition: ['dutyDefinition', 'user'],
    definitionsGeneralSystemProcess: ['process', 'unit'],

    definitionsDocumentDocumentType: ['documentType'],
    definitionsDocumentClassification: ['classification'],

    ticketCategory: ['ticketCategory'],
    ticketSLA: ['ticketSLA'],
    /** Main ticketing module — pickers need categories/SLAs/root causes without narrower page keys. */
    ticketing: ['ticketCategory', 'ticketSLA', 'ticket', 'rootCause', 'user', 'unit', 'team', 'role'],
    /** My Work assignee / org pickers. */
    myWork: ['user', 'unit', 'team', 'role'],

    vendorManagementVendors: ['vendor', 'user'],
    vendorManagementAssessments: ['vendor', 'vendorAssessment', 'formTemplate'],

    definitionsKvkkLegalReason: ['legalReason'],
    definitionsKvkkCollectionMethod: ['collectionMethod'],
    definitionsKvkkProcessingPurpose: ['processingPurpose'],
    definitionsKvkkSubjectPersonGroup: ['subjectPersonGroup'],
    definitionsKvkkCategory: ['personalDataCategory'],

    definitionsComplianceRegulatorySources: ['regulatorySource'],
    definitionsComplianceClauses: ['regulatoryClause', 'regulatorySource'],
    definitionsComplianceObligations: ['regulatoryObligation', 'regulatoryClause', 'regulatorySource'],
    definitionsComplianceFrameworks: ['complianceFramework', 'regulatorySource'],

    kvkkRetentionPeriod: ['privacyRetentionPeriod'],
    gdprDisposal: ['disposalRun', 'dataDisposalMethod', 'personalDataInventory', 'personalDataCategory', 'user'],
    gdprDataSubjectRequest: ['dataSubjectRequest'],
    gdprDataBreach: ['dataBreach', 'personalDataCategory'],
    definitionsPrivacyDpiaTemplates: ['privacyDpiaTemplate'],

    definitionsGovernanceReports: ['governanceReportDefinition'],
    definitionsIntegrationConnectors: ['integrationConnector'],
    definitionsSurveys: ['formTemplate'],

    definitionsGeneralSystemRootCause: ['rootCause'],
    definitionsGeneralSystemMailTemplate: ['mailTemplate'],

    definitionsKvkkTransferType: ['transferType'],
    definitionsKvkkInternationalTransfer: ['internationalTransfer'],
    kvkkRecipientGroup: ['recipientGroup'],
    kvkkDataDisposalMethod: ['dataDisposalMethod'],
    kvkkDataSource: ['dataSource'],
    kvkkPrivacyPrinciple: ['privacyPrinciple'],
    kvkkSecurityMeasure: ['securityMeasure'],

    definitionsApplicationTrackingApplicationMethod: ['applicationMethod', 'subjectPersonGroup'],
    definitionsApplicationTrackingApplicationRequestInformation: ['applicationRequestInformation'],

    definitionsObtainingExplicitConsentMethod: ['explicitConsentMethod'],
    definitionsObtainingExplicitConsentReason: ['explicitConsentReason'],

    gdprApplicationTracking: ['applicationTracking', 'applicationMethod', 'applicationRequestInformation', 'subjectPersonGroup'],
    gdprObtainingExplicitConsent: ['explicitConsent', 'explicitConsentMethod', 'explicitConsentReason', 'personalDataCategory', 'processingPurpose'],
    gdprConsentRecord: ['consentRecord', 'explicitConsent', 'processingPurpose'],
    gdprCookieInventory: ['cookieInventory', 'privacyNotice', 'explicitConsent'],
    gdprPrivacyNotice: ['privacyNotice', 'personalDataInventory'],
    gdprDpiaAssessment: ['dpiaAssessment', 'privacyDpiaTemplate', 'personalDataInventory', 'internationalTransfer'],
    gdprDpa: ['dataProcessingAgreement', 'vendor', 'internationalTransfer'],
    ticketTemplate: ['ticketTemplate', 'ticketCategory', 'ticketSLA', 'workflowTemplate', 'assignmentTemplate', 'serviceCatalogItem'],

    definitionsKvkkCompany: ['kvkkCompany', 'user'],

    userOperationsDelegations: ['delegation', 'user'],
    /** Admin delegation list (`/admin/delegations`) — user names come populated; only the list entity syncs. */
    adminDelegations: ['delegation'],
    platformPartyTemplates: ['partyTemplate'],
    platformWorkflowTemplates: ['workflowTemplate'],
    platformAssignmentTemplates: ['assignmentTemplate'],
    collectionCampaigns: ['user', 'team', 'unit', 'role'],

    isoContext: ['context'],
    isoCommunication: ['communication'],
    isoMeeting: ['meeting'],
    contextManagementInterestedParties: ['interestedParty'],
    contextManagementScopeDocument: ['scopeDocument'],

    ismsControlsControl: ['control'],
    /** SoA list syncs on `control`; `threat` itself is core — ThreatForm renders `AssetSelect`. */
    ismsControlsSoa: ['control'],
    ismsControlsThreatIntelligence: ['asset'],
    ismsControlsObjective: ['objective'],
    ismsControlsCompetence: ['competence'],
    ismsControlsAssessmentQuestions: ['assessmentQuestion'],
    ismsControlsVulnerability: ['vulnerability'],
    ismsControlsTraining: ['training'],
    isoCompetence: ['competence'],
    isoTraining: ['training'],
    isoMyTraining: ['training'],
    isoObjective: ['objective'],
    internalControlControl: ['control'],
    internalAuditAssessmentQuestions: ['assessmentQuestion'],
    internalAuditAssessment: ['assessment'],
    internalAuditMyAudits: ['assessment'],

    auditComplianceAuditAssessment: ['assessment'],
    incidentManagementIncidents: ['incident', 'incidentTemplate', 'ticketCategory', 'rootCause', 'user', 'unit', 'team'],
    incidentManagementActions: ['action', 'user', 'unit'],
    /** Registry key for `/incident-management/actions` (RiskTreatmentForm `UserSelect` / `UnitSelect`, `UnitLabel`). */
    incidentActions: ['action', 'user', 'unit'],
    itsmChangeRequest: ['changeRequest'],
    itsmProblem: ['problem', 'ticket', 'serviceCatalogItem', 'rootCause'],
    itsmServiceCatalog: ['serviceCatalogItem', 'user', 'team', 'unit', 'role'],
    itsmSupplierContracts: ['vendor'],

    bcmBia: ['bia', 'vendor', 'unit', 'user'],
    bcmBcp: ['bcp', 'vendor', 'unit', 'user'],
    bcmCrisisEvent: ['crisisEvent', 'vendor'],
    bcmExercises: ['exercise', 'vendor'],
    bcmExercise: ['exercise', 'vendor'],

    reportDocument: ['document', 'documentType', 'classification', 'user', 'unit', 'vendor'],
    reportForms: ['formTemplate'],
    reportArchive: ['reportArchive'],
    communicationProvider: ['communicationProvider'],
    platformThirdPartyApplications: ['integrationConnection'],
    adminDeadLetterQueue: ['deadLetterEvent'],
    definitionsSurveyResponses: ['formSubmission'],
    bigdesRoadMap: ['bigdesRoadMapWorkPackage'],
    bigdesCompensatoryControl: ['bigdesCompensatoryControl'],
    bigdesMeasureApplicability: ['assetGroupMeasureApplicability'],
    bigdesCurrentSituationAnalysis: ['assetGroupMeasureApplicability'],
    bigdesCurrentSituationEvaluation: ['assetGroupMeasureApplicability'],
    platformScheduledReportDistribution: ['detailedReportProfile', 'user', 'team', 'unit'],
    platformTailorability: [
        'customFieldDefinition',
        'entityFormLayout',
        'customPermissionDefinition',
        'tenantFeatureFlag',
    ],
    adminWebhooks: ['webhookSubscription'],
    adminPendingWorkflowOperations: ['workflowInstance'],
    adminTenantCompliance: ['complianceFramework'],

    riskOperationsRiskRegister: ['unit', 'user', 'team', 'process', 'threat', 'rootCause', 'control'],
    riskOperationsTreatment: ['unit', 'user'],
    riskOperationsMonitoring: ['riskRegister', 'riskTreatment'],
    riskOperationsResidualRiskAcceptance: ['unit', 'user', 'team', 'process', 'threat', 'rootCause', 'control'],
    riskOperationsRiskOpportunities: ['unit', 'user', 'team', 'process', 'threat', 'rootCause', 'control'],
});
